import { useCallback, useEffect, useRef, useState } from 'react';
import { putToSignedUrl } from '../api';
import { PrinterCamera, usePrinterCamera, type Still } from '../components/PrinterCamera';
import { useWorkstation } from '../auth';
import { QrCode } from '../components/QrCode';
import { PRINTER_ID, printerIdFrom } from '../ids';
import {
  CAMERA_LABELS, CALIBRATION_SHOTS, EXPECTED_CROSSES, LENS_SHOTS, USB_LENS_SHOTS, lensCaptureUrl, EXPECTED_MARKERS, barMoved, countAgainst,
  movementHeading, PREFERRED_PRINTER,
} from '../printers';
import {
  lensPending, photosStillNeeded, surveyPending, type CalibrationSession, type CaptureCamera, type PrinterCalibration, type PrinterDetail, type PrinterSummary,
} from '../model';

const POLL_MS = 3000;

/**
 * One upload session shot by shot from the printer camera, the way the phone
 * pages do it: the session opens on the first shot, each photograph goes to
 * the next signed URL as it is taken, and the last one landing starts the
 * solver. Uploading as it goes keeps twelve 48 MP stills out of memory.
 */
function useCameraSession(open: () => Promise<CalibrationSession>,
                          after: () => Promise<unknown>) {
  const [session, setSession] = useState<CalibrationSession | null>(null);
  const [shot, setShot] = useState(0);

  async function send(photo: Still) {
    const current = session ?? await open();
    await putToSignedUrl(current.uploads[shot].url, photo.blob, 'image/jpeg');
    if (shot + 1 >= current.photos) {
      setSession(null);
      setShot(0);
    } else {
      setSession(current);
      setShot(shot + 1);
    }
    await after();
  }
  const reset = useCallback(() => { setSession(null); setShot(0); }, []);
  return { shot, started: session !== null, send, reset };
}

/**
 * The five numbers, in the solver's own key names.
 *
 * Only the length is required. The other four are reported when present and
 * reported as *absent* when not -- the stand-off correction is about 1% per
 * 5 mm and the report says it was not applied, whereas a wrong length is a
 * quarter inch across a sheet with nothing to say so.
 *
 * Thickness and stand-off are two fields because they are two measurements.
 * They agree only when the bars rest on the mesh plane itself; on the MO-180
 * the bars sit on a foam skirt about 9.7 mm below the platen, where a 10.2 mm
 * bar stands 0.5 mm proud. Entering 10.2 as the stand-off there is a 1.7%
 * error in the one number that scales every sheet.
 */
const BAR_FIELDS: { key: keyof PrinterCalibration; label: string; hint: string;
                    signed?: boolean }[] = [
  { key: 'bar_length_mm', label: 'Bar length (mm)',
    hint: 'Required. 488.0 mm is the Rev C printed bar; 457.2 mm is exactly 18 inches.' },
  { key: 'bar_width_mm', label: 'Bar width (mm)',
    hint: 'Recorded and reported; not used in the fit yet.' },
  { key: 'bar_thickness_mm', label: 'Bar thickness (mm)',
    hint: 'The bar itself, top face to underside. Recorded, not used in the fit.' },
  { key: 'bar_standoff_mm', label: 'Bar stand-off (mm)', signed: true,
    hint: 'Top faces above the mesh plane. Measured on the bed, not from the bar: '
        + 'negative if they sit below it, 0 if flush. This is the parallax term.' },
  { key: 'camera_distance_mm', label: 'Camera distance (mm)',
    hint: 'Blank unless measured — otherwise EXIF supplies it.' },
];

/**
 * Set a printer up, and check it is still set up (F8).
 *
 * Three steps, once per install: print the calibration sheet, photograph it on
 * the bed, upload it. The photograph is normally taken through a QR code on the
 * phone (F11), which uploads it directly; the file picker stays for a
 * photograph that is already on this computer. It contains both the printed crosses --
 * which are at page coordinates the printer was told to use -- and the
 * fiducial markers, so it ties the markers into the printer's own coordinates.
 * Everything afterwards is one photo and one print per sheet.
 *
 * The second reason to be here is the one that costs canvas. The residual of a
 * single production photograph **cannot** detect a bar that has been
 * translated: markers on one bar are collinear, and translating a line is a
 * shear, which a homography absorbs exactly. Two surveys can, because both
 * measure the bars against the printer itself. So "run it again and compare"
 * is not housekeeping, it is the only check there is, and its result is shown
 * below in mils.
 */
export function Printers() {
  const api = useWorkstation();
  const [printers, setPrinters] = useState<PrinterSummary[]>([]);
  const [detail, setDetail] = useState<PrinterDetail | null>(null);
  const [selected, setSelected] = useState<string>(PREFERRED_PRINTER);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [capture, setCapture] =
    useState<{ url: string; expiresAt: string } | null>(null);
  const [bars, setBars] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [newName, setNewName] = useState('');
  const [adding, setAdding] = useState(false);
  const [savingCamera, setSavingCamera] = useState(false);
  const camera = usePrinterCamera();
  // Which printer the bar form was last filled from. `load` runs on every
  // poll, and a poll that refilled the form would wipe what is being typed.
  const formFor = useRef<string | null>(null);

  const load = useCallback(async () => {
    try {
      const list = await api.listPrinters();
      setPrinters(list.printers);
      // `selected` starts as PREFERRED_PRINTER, which is a printer that need not
      // exist on every stage. Land on a real one rather than showing this page
      // for a printer that is not there, with a dropdown whose value matches
      // none of its options.
      if (list.printers.length
          && !list.printers.some((p) => p.printerId === selected)) {
        setSelected(list.printers[0].printerId);
        return;                 // the effect reruns, keyed on the new id
      }
      const found = await api.getPrinter(selected).catch(() => null);
      setDetail(found);
      // The form shows what is stored; blank means "not measured", which for
      // the three optional numbers is a real answer and not a missing one.
      // Filled once per printer, and again after a save, never by a poll.
      if (formFor.current !== selected) {
        formFor.current = selected;
        setBars(Object.fromEntries(BAR_FIELDS.map(({ key }) => {
          const value = found?.calibration?.[key];
          return [key, value == null ? '' : String(value)];
        })));
      }
    } catch (e) { setError((e as Error).message); }
  }, [selected]);

  useEffect(() => { void load(); }, [load]);
  const cameraLens = useCameraSession(() => api.lensUpload(selected), load);
  const cameraSurvey = useCameraSession(() => api.calibrationUpload(selected), load);
  // A QR code is for one printer; switching printers must not leave the last
  // one's code on screen under the new one's name. The new printer's code is
  // shown straight away (SLD-0004): this page exists to be scanned, and the
  // button was one more click on the way to the phone. Keyed on whether the
  // printer is known, not on the list: the list is a new array on every poll,
  // and a code on screen is what keeps the poll running, so keying on it
  // minted a new code every few seconds under the phone trying to scan it.
  const known = printers.some((p) => p.printerId === selected);
  useEffect(() => {
    setCapture(null);
    if (known) void showQr();
  }, [selected, known]);
  // A half-shot camera session belongs to the old printer. Kept apart from
  // the QR code's effect so neither can take the other with it.
  useEffect(() => {
    cameraLens.reset();
    cameraSurvey.reset();
  }, [selected, cameraLens.reset, cameraSurvey.reset]);

  // Polled while a survey is running, and for as long as a QR code is on
  // screen: the photograph may arrive from the phone at any moment, and this
  // screen is where its result is meant to appear.
  const surveying = detail ? surveyPending(detail) : false;
  const measuringLens = detail ? lensPending(detail) : false;
  useEffect(() => {
    if (!surveying && !measuringLens && !capture) return;
    const timer = setInterval(() => { void load(); }, POLL_MS);
    return () => clearInterval(timer);
  }, [surveying, measuringLens, capture, load]);

  async function upload(files: File[]) {
    setError(null);
    if (files.length !== CALIBRATION_SHOTS.length) {
      setError(CALIBRATION_SHOTS.length > 1
        ? `Choose ${CALIBRATION_SHOTS.length} photos of the calibration sheet `
          + `together; ${files.length} were chosen.`
        : `Choose one photo of the calibration sheet; ${files.length} were chosen.`);
      return;
    }
    setUploading(true);
    try {
      const session = await api.calibrationUpload(selected);
      await Promise.all(files.map((file, i) =>
        putToSignedUrl(session.uploads[i].url, file, 'image/jpeg')));
      await load();
    } catch (e) { setError((e as Error).message); }
    finally { setUploading(false); }
  }

  async function uploadLens(files: File[]) {
    setError(null);
    if (files.length !== LENS_SHOTS.length) {
      setError(`Choose ${LENS_SHOTS.length} photos of the lens board together; `
        + `${files.length} were chosen.`);
      return;
    }
    setUploading(true);
    try {
      const session = await api.lensUpload(selected);
      await Promise.all(files.map((file, i) =>
        putToSignedUrl(session.uploads[i].url, file, 'image/jpeg')));
      await load();
    } catch (e) { setError((e as Error).message); }
    finally { setUploading(false); }
  }

  async function showQr() {
    setError(null);
    try {
      setCapture(await api.printerCaptureToken(selected));
    } catch (e) { setError((e as Error).message); }
  }

  async function saveBars() {
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      const length = Number(bars.bar_length_mm);
      if (!Number.isFinite(length) || length <= 0) {
        throw new Error('the bar length is required, in millimetres');
      }
      const calibration: PrinterCalibration = { bar_length_mm: length };
      for (const { key, signed } of BAR_FIELDS) {
        // Skipped by name rather than by position: the length is required and
        // already set, and the compiler needs to know the rest are nullable.
        if (key === 'bar_length_mm') continue;
        const raw = bars[key]?.trim();
        // The stand-off is a signed position, so 0 and -7.7 are answers rather
        // than typos. Everything else is a length of an object.
        if (raw && !(signed ? Number.isFinite(Number(raw)) : Number(raw) > 0)) {
          throw new Error(signed
            ? `${key} must be a number, or blank`
            : `${key} must be a positive number, or blank`);
        }
        // Cleared means "we never measured this", which the solver reports
        // rather than refusing. Sent as null so it overwrites an old value.
        calibration[key] = raw ? Number(raw) : null;
      }
      await api.putPrinter(selected, { calibration });
      setSaved(true);
      formFor.current = null;
      await load();
    } catch (e) { setError((e as Error).message); }
    finally { setSaving(false); }
  }

  // Saved on change: one choice, nothing to type, so no Save button.
  async function saveCamera(camera: CaptureCamera | null) {
    setSavingCamera(true);
    setError(null);
    try {
      await api.putPrinter(selected, { camera });
      await load();
    } catch (e) { setError((e as Error).message); }
    finally { setSavingCamera(false); }
  }

  async function addPrinter() {
    setAdding(true);
    setError(null);
    try {
      const { printerId } = await api.createPrinter({
        printerId: newId, name: newName.trim(),
      });
      setNewName('');
      // Selecting it is enough to reload: `load` is keyed on `selected`, so
      // the effect refetches the list and the new printer's (empty) detail.
      setSelected(printerId);
    } catch (e) { setError((e as Error).message); }
    finally { setAdding(false); }
  }

  const survey = detail?.survey;
  const moved = survey?.movedSinceLast;
  const calibrated = Boolean(detail?.calibration?.bar_length_mm);
  const newId = printerIdFrom(newName);
  const newIdUsable = PRINTER_ID.test(newId);
  const taken = printers.some((p) => p.printerId === newId);

  return (
    <div className="stack">
      <section className="panel">
        <h2>Printers</h2>
        {printers.length > 0 ? (
          <label>
            Printer
            <select value={selected} onChange={(e) => setSelected(e.target.value)}>
              {printers.map((p) => (
                <option key={p.printerId} value={p.printerId}>{p.name}</option>
              ))}
            </select>
          </label>
        ) : (
          <p className="muted">
            No printers yet. Add the bed you are setting up, then work down
            this page.
          </p>
        )}

        {detail && (
          <label>
            Sheet photos taken with
            <select value={detail.camera ?? ''} disabled={savingCamera}
                    onChange={(e) => void saveCamera(
                      (e.target.value || null) as CaptureCamera | null)}>
              <option value="">Not set: offer both</option>
              {(Object.keys(CAMERA_LABELS) as CaptureCamera[]).map((c) => (
                <option key={c} value={c}>{CAMERA_LABELS[c]}</option>
              ))}
            </select>
            <span className="muted">
              The job page offers only this one for jobs on this printer. Use
              the camera the survey and lens were measured with.
            </span>
          </label>
        )}

        <div className="fields">
          <label>
            Add a printer
            <input value={newName} placeholder="MO-180 front"
                   onChange={(e) => setNewName(e.target.value)} />
            <span className={taken ? 'error' : 'muted'}>
              {newName.trim() === ''
                ? 'Its name, as it will read in this list.'
                : taken
                  ? `There is already a printer with the id ${newId}.`
                  : newIdUsable
                    ? `Will be created as ${newId}.`
                    : 'That name has too little in it to make an id from '
                      + '\u2014 two or more letters or digits.'}
            </span>
          </label>
        </div>
        <button className="button" disabled={adding || !newIdUsable || taken}
                onClick={() => void addPrinter()}>
          {adding ? 'Adding\u2026' : 'Add printer'}
        </button>
        <p className="muted">
          A new printer starts with no bar dimensions and no survey, so it
          refuses every sheet until both are done. That is steps 1 to 3 below,
          and it is deliberate: a printer that looked ready would scale sheets
          by a number nobody measured.
        </p>
      </section>

      <section className="panel">
        <h3>1. The bed&rsquo;s bar dimensions</h3>
        <p className="muted">
          Measured off this printer&rsquo;s own bars, in millimetres. The bar
          length is the only known length in a photograph, so it sets the scale
          of everything downstream &mdash; the detected mesh count, which is a
          refusal test, and where every stitch lands. Nothing is assumed: a
          printer with no length recorded refuses sheets rather than guessing.
        </p>
        <div className="fields">
          {BAR_FIELDS.map(({ key, label, hint }) => (
            <label key={key}>
              {label}
              <input type="number" step="0.1" min="0" inputMode="decimal"
                     value={bars[key] ?? ''}
                     onChange={(e) => {
                       setSaved(false);
                       setBars({ ...bars, [key]: e.target.value });
                     }} />
              <span className="muted">{hint}</span>
            </label>
          ))}
        </div>
        <button className="button" disabled={saving} onClick={() => void saveBars()}>
          {saving ? 'Saving…' : 'Save bar dimensions'}
        </button>
        {saved && <p className="muted">Saved.</p>}
        {!calibrated && (
          <p className="error">
            This printer has no bar length recorded, so every sheet on it is
            refused before it is processed.
          </p>
        )}
      </section>

      <section className="panel">
        <h3>2. Measure the camera&rsquo;s lens</h3>
        <p className="muted">
          The lens bends the picture &mdash; by about a millimetre in the
          middle of the sheet, which is enough to put the stitches off there.
          Print the board below and photograph it {LENS_SHOTS.length} ways
          with <strong>the camera that photographs every sheet</strong>: the
          printer camera, or the phone at 1x. Every photo from then on is
          corrected for it. Do this again if the camera changes or is moved
          on the lid.
        </p>
        <a className="button" href="/lens-board.pdf" download>
          Download lens board (PDF)
        </a>
        <h4>With the printer camera</h4>
        <PrinterCamera
          camera={camera}
          actions={measuringLens && !cameraLens.started ? [] : [{
            key: 'lens',
            label: `Shoot photo ${cameraLens.shot + 1} of ${USB_LENS_SHOTS.length}`,
          }]}
          onPhoto={(_, photo) => cameraLens.send(photo)}>
          <p className="muted">
            The camera cannot move, so the board does. Tape the printed board
            flat to a rigid panel &mdash; foam board or acrylic &mdash; so it
            stays flat when lifted, and move it as each shot asks.
          </p>
          {!(measuringLens && !cameraLens.started) && (
            <p><strong>Photo {cameraLens.shot + 1}:</strong>{' '}
              {USB_LENS_SHOTS[cameraLens.shot]}</p>
          )}
          {cameraLens.started && (
            <div className="button-row">
              <button onClick={cameraLens.reset}>Start again from photo 1</button>
            </div>
          )}
        </PrinterCamera>
        <h4>Or with the phone</h4>
        {capture ? (
          <div className="qr-block">
            <QrCode url={lensCaptureUrl(capture.url)} />
            <p className="muted">
              Scan with the phone at the printer; the page walks through the{' '}
              {LENS_SHOTS.length} shots.
            </p>
          </div>
        ) : (
          <p className="muted">Show the QR code in step 4 to get this one too.</p>
        )}
        <label className={`button file-button ${uploading ? 'disabled' : ''}`}>
          <input type="file" accept="image/*" multiple disabled={uploading}
                 onChange={(e) => {
                   const files = Array.from(e.target.files ?? []);
                   e.target.value = '';
                   if (files.length) void uploadLens(files);
                 }} />
          {uploading
            ? 'Uploading…'
            : `Or upload ${LENS_SHOTS.length} board photos from this computer`}
        </label>
        {measuringLens && detail && (
          <p className="busy-text">
            <span className="spinner" aria-hidden />
            {detail.lensPhotosReceived < detail.lensPhotos
              ? `Waiting for photos: ${detail.lensPhotosReceived} of ${detail.lensPhotos} received.`
              : 'Measuring the lens…'}
          </p>
        )}
        {detail?.lensError && (
          <div className="panel stop">
            <h4>The lens could not be measured</h4>
            <p>{detail.lensError.message}</p>
          </div>
        )}
        {detail?.lens ? (
          <dl className="facts">
            <div><dt>Measured</dt>
              <dd>{new Date(detail.lens.created).toLocaleString()}</dd></div>
            <div><dt>From</dt>
              <dd>{detail.lens.photosUsed} photos, {detail.lens.corners} corners</dd></div>
            <div><dt>Frame covered</dt>
              <dd>{Math.round(detail.lens.coverage * 100)}%</dd></div>
            <div><dt>Predicts an unseen photo to</dt>
              <dd>{detail.lens.heldOutPx.toFixed(2)} px</dd></div>
          </dl>
        ) : (
          <p className="warn-text">
            This printer&rsquo;s camera lens has not been measured, so photos
            are used as taken and prints can be off in the middle.
          </p>
        )}
      </section>

      <section className="panel">
        <h3>3. Print the calibration sheet</h3>
        <p className="muted">
          One sacrificial sheet on the bed, printed at 100% with no scaling or
          centring in the RIP. Everything after this depends on it going
          through the printer untouched.
        </p>
        <a className="button" href="/calibration-sheet.pdf" download>
          Download calibration sheet (PDF)
        </a>
      </section>

      <section className="panel">
        <h3>4. Photograph it on the bed</h3>
        <p className="muted">
          Leave it where it was printed. Both registration bars in frame, no
          glare, square to the bed — the same framing as a production photo,
          with the same camera the lens was measured with.
        </p>
        <h4>With the printer camera</h4>
        <PrinterCamera
          camera={camera}
          actions={surveying && !cameraSurvey.started ? [] : [{
            key: 'survey',
            label: CALIBRATION_SHOTS.length > 1
              ? `Shoot photo ${cameraSurvey.shot + 1} of ${CALIBRATION_SHOTS.length}`
              : 'Shoot the calibration sheet',
          }]}
          onPhoto={(_, photo) => cameraSurvey.send(photo)}>
          <p className="muted">
            Open the lid fully and check the preview shows both bars and every
            marker sticker, with the bed lit as it is for every sheet.
          </p>
        </PrinterCamera>
        <h4>Or with the phone</h4>
        {capture && (
          <div className="qr-block">
            <QrCode url={capture.url} />
            <p className="muted">
              Scan with the phone at the printer and shoot the calibration
              sheet there. The link works for this printer only and expires{' '}
              {new Date(capture.expiresAt).toLocaleString()}. The result
              appears below as well as on the phone.
            </p>
          </div>
        )}
        {/* Side by side, the phone first: the file picker is the last resort (SLD-0004). */}
        <div className="button-row">
          {!capture && (
            <button className="primary" disabled={!detail}
                    onClick={() => void showQr()}>
              Show QR code for the phone
            </button>
          )}
          <label className={`button file-button ${uploading ? 'disabled' : ''}`}>
            <input type="file" accept="image/*" disabled={uploading}
                   multiple={CALIBRATION_SHOTS.length > 1}
                   onChange={(e) => {
                     const files = Array.from(e.target.files ?? []);
                     e.target.value = '';
                     if (files.length) void upload(files);
                   }} />
            {uploading
              ? 'Uploading…'
              : CALIBRATION_SHOTS.length > 1
                ? `Or upload ${CALIBRATION_SHOTS.length} photos from this computer`
                : 'Or upload a photo from this computer'}
          </label>
        </div>
        {surveying && detail && photosStillNeeded(detail) > 0 && (
          <p className="busy-text">
            <span className="spinner" aria-hidden />
            {detail.calibrationPhotos > 1
              ? `Waiting for photos: ${detail.calibrationPhotosReceived} of `
                + `${detail.calibrationPhotos} received.`
              : 'Waiting for the photo…'}
          </p>
        )}
        {surveying && detail && photosStillNeeded(detail) === 0 && (
          <p className="busy-text">
            <span className="spinner" aria-hidden />
            Surveying…
          </p>
        )}
      </section>

      <section className="panel">
        <h3>5. Result</h3>
        {detail?.surveyStale && (
          <div className="panel stop">
            <h4>Photograph the calibration print again</h4>
            <p>
              The camera&rsquo;s lens was measured after this printer was last
              calibrated, so the bars were found through a different
              correction from the one every sheet now gets. Every sheet is
              refused until the calibration print is photographed again.
            </p>
          </div>
        )}
        {detail?.surveyError && (
          <div className="panel stop">
            <h4>The survey failed</h4>
            <p>{detail.surveyError.message}</p>
            <p className="muted">
              Usually the crosses or the markers could not be found in one of the
              photos. Check the print actually came out, then photograph it again.
            </p>
          </div>
        )}
        {!survey && !detail?.surveyError && (
          <p className="muted">
            This printer has never been surveyed. Nothing can be printed on it
            until it has been.
          </p>
        )}
        {survey && (
          <>
            <dl className="facts">
              <div><dt>Surveyed</dt>
                <dd>{new Date(survey.created).toLocaleString()}</dd></div>
              <div><dt>Markers found</dt>
                <dd>{countAgainst(survey.markers, EXPECTED_MARKERS)}</dd></div>
              {survey.photos > 1 && (
                <div><dt>Photos</dt><dd>{survey.photos}</dd></div>
              )}
              <div><dt>Crosses used</dt>
                <dd>{countAgainst(survey.crossesFound, EXPECTED_CROSSES)}
                  {survey.photos > 1 ? ' in the worst photo' : ''}</dd></div>
              <div><dt>Residual</dt>
                <dd>{survey.residualMil.toFixed(2)} mil rms,{' '}
                  {survey.residualMaxMil.toFixed(2)} mil worst</dd></div>
              {survey.markerSpreadMil !== null && (
                <div><dt>Photos agree to</dt>
                  <dd>{survey.markerSpreadMil.toFixed(1)} mil at the worst marker</dd></div>
              )}
            </dl>
            <p className="muted">
              The budget is 10 mil rms by design and 18.5 mil hard, and
              everything downstream spends from the same purse.
            </p>
            {survey.notes.map((note) => (
              <p key={note} className="muted">{note}</p>
            ))}
            {moved && (
              // Over budget stays a stop: sheets printed since were off.
              <div className={barMoved(moved) ? 'panel stop' : 'panel info'}>
                <h4>{movementHeading(moved.previousCreated)}</h4>
                <p>
                  Worst: marker {moved.worstMarker} moved{' '}
                  <strong>{moved.worstMil.toFixed(1)} mil</strong> across{' '}
                  {moved.markersCompared} markers.
                </p>
                {barMoved(moved) && (
                  <p>
                    Every sheet printed against the old survey was wrong by
                    roughly 0.8 times that, and nothing would have reported it.
                    This comparison is the only check that can see a bar move.
                  </p>
                )}
              </div>
            )}
          </>
        )}
      </section>

      {error && <p className="error">{error}</p>}
    </div>
  );
}
